"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import NoteDropdown from "@/components/note-dropdown";
import { useProgressNavigation } from "@/components/progress-bar";
import { Badge } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { useNoteTemplates } from "@/hooks/use-selected-note";
import { formatDate, formatTime } from "@/lib/format-data";
import { getProviderNames } from "@/lib/get-provider-names";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import type { NoteListItem } from "@/types/notes";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface Props {
  patientId: string;
  selectedEventId?: string;
  enabled?: boolean;
}

/** The row's own date, so the list reads in the order it is labelled. */
function noteSortDate(note: NoteListItem): number {
  const date = note.visitDate ?? note.createdDate;
  return date ? new Date(date).getTime() : 0;
}
export default function NotesSection(t0) {
  const $ = _c(43);
  const {
    patientId,
    selectedEventId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    currentUser
  } = useUser();
  let t2;
  if ($[0] !== currentUser) {
    t2 = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
    $[0] = currentUser;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canViewNotes = t2;
  let t3;
  if ($[2] !== patientId) {
    t3 = noteQueryOptions.sidebarPreview(patientId);
    $[2] = patientId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = enabled && !!patientId && canViewNotes;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: recentNotesPage,
    isPending
  } = useQuery(t5);
  recentNotesPage?.data;
  let t6;
  if ($[7] !== recentNotesPage?.data) {
    t6 = recentNotesPage?.data ?? [];
    $[7] = recentNotesPage?.data;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const recentNotes = t6;
  let t7;
  if ($[9] !== recentNotes || $[10] !== selectedEventId) {
    let t8;
    if ($[12] !== selectedEventId) {
      t8 = note => note.eventId === selectedEventId;
      $[12] = selectedEventId;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    t7 = recentNotes.some(t8);
    $[9] = recentNotes;
    $[10] = selectedEventId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const selectedNoteInRecent = t7;
  let t8;
  if ($[14] !== patientId || $[15] !== selectedEventId) {
    t8 = noteQueryOptions.listByEvents(patientId, selectedEventId ? [selectedEventId] : [], 1);
    $[14] = patientId;
    $[15] = selectedEventId;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const selectedNoteQuery = t8;
  const shouldFetchSelectedNote = selectedNoteQuery.enabled && canViewNotes && enabled && !isPending && !selectedNoteInRecent;
  let t9;
  if ($[17] !== selectedNoteQuery || $[18] !== shouldFetchSelectedNote) {
    t9 = {
      ...selectedNoteQuery,
      enabled: shouldFetchSelectedNote
    };
    $[17] = selectedNoteQuery;
    $[18] = shouldFetchSelectedNote;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const {
    data: selectedNotePage,
    isPending: isSelectedNotePending
  } = useQuery(t9);
  selectedNotePage?.data;
  let t10;
  if ($[20] !== recentNotes || $[21] !== selectedEventId || $[22] !== selectedNotePage?.data) {
    const selectedNotes = selectedNotePage?.data.slice(0, 1) ?? [];
    const selectedNoteIds = new Set(selectedNotes.map(_temp));
    const combined = [...selectedNotes, ...recentNotes.filter(note_1 => !selectedNoteIds.has(note_1.id))];
    let t11;
    if ($[24] !== selectedEventId) {
      t11 = (a, b) => {
        const aIsSelected = a.eventId === selectedEventId ? 1 : 0;
        const bIsSelected = b.eventId === selectedEventId ? 1 : 0;
        if (aIsSelected !== bIsSelected) {
          return bIsSelected - aIsSelected;
        }
        const aDate = noteSortDate(a);
        const bDate = noteSortDate(b);
        return bDate - aDate;
      };
      $[24] = selectedEventId;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = combined.sort(t11);
    $[20] = recentNotes;
    $[21] = selectedEventId;
    $[22] = selectedNotePage?.data;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const notes = t10;
  const totalNotes = recentNotesPage?.total ?? notes.length;
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    templates
  } = useNoteTemplates();
  const suppressNextNoteNavRef = useRef(false);
  if (!canViewNotes) {
    let t11;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <p className="text-caption text-muted-foreground">You don't have permission to view notes</p>;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  if (isPending || shouldFetchSelectedNote && isSelectedNotePending) {
    let t11;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <PatientSidebarSectionSkeleton />;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    return t11;
  }
  if (notes.length === 0) {
    let t11;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <p className="text-caption text-muted-foreground">No notes created</p>;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  let t11;
  if ($[29] !== navigateWithProgress || $[30] !== notes || $[31] !== selectedEventId || $[32] !== templates) {
    t11 = notes.map(note_2 => <div key={note_2.id} className={cx("flex items-center justify-between", "mb-3 rounded-md p-1 px-2", "transition-all", "*:hover:bg-transparent", "hover:bg-accent/50", !!selectedEventId && note_2.eventId === selectedEventId && "rounded-md bg-accent hover:bg-accent")}><div className="grid min-w-0 flex-1 cursor-pointer rounded-md hover:bg-background-secondary" onClick={() => {
        if (suppressNextNoteNavRef.current) {
          return;
        }
        navigateWithProgress(`/scribe/playground/${note_2.id}`);
      }}><div className="flex items-start justify-between gap-2"><p className="line-clamp-3 min-w-0 break-words text-body-strong text-foreground">{note_2.title || note_2.summary || "Note"}</p>{selectedEventId && note_2.eventId === selectedEventId && <Badge className="flex-shrink-0 text-caption">Selected Event</Badge>}</div>{note_2.templateId && <p className="break-words text-caption text-foreground-secondary">{templates?.find(t => t.id === note_2.templateId)?.name || note_2.templateId}</p>}{(note_2.providers || []).length > 0 && <p className="truncate text-caption text-foreground-secondary">By {getProviderNames(note_2.providers)}</p>}{note_2.visitDate && <p className="truncate text-caption text-foreground-secondary">Visit {formatDate(note_2.visitDate)} {formatTime(note_2.visitDate)}</p>}</div><div className="inline w-fit flex-shrink-0"><NoteDropdown note={note_2} className="hover:!bg-accent/50" suppressNextRowClickRef={suppressNextNoteNavRef} /></div></div>);
    $[29] = navigateWithProgress;
    $[30] = notes;
    $[31] = selectedEventId;
    $[32] = templates;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== navigateWithProgress || $[35] !== notes.length || $[36] !== patientId || $[37] !== recentNotes.length || $[38] !== totalNotes) {
    t12 = totalNotes > recentNotes.length && <div className="mt-4 border-t pt-3 text-center"><p className="text-caption text-foreground-secondary">{notes.length > recentNotes.length ? `Showing ${notes.length} notes: ${recentNotes.length} of ${totalNotes}, plus the selected event note` : `Showing ${recentNotes.length} of ${totalNotes} notes`}</p><button type="button" className="mt-1 text-caption font-medium text-primary hover:underline" onClick={() => navigateWithProgress(`/patient/${patientId}?tab=medical-notes`)}>View full note history</button></div>;
    $[34] = navigateWithProgress;
    $[35] = notes.length;
    $[36] = patientId;
    $[37] = recentNotes.length;
    $[38] = totalNotes;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12) {
    t13 = <div>{t11}{t12}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
}
function _temp(note_0) {
  return note_0.id;
}